import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiPencil, HiEye, HiTrash, HiPlus, HiUserGroup, HiDocumentText } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmationModal from '../components/ConfirmationModal';
import Tooltip from '../components/Tooltip';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { formatPKR } from '../utils/currencyFormatter';
import { API } from '../config';
import axios from 'axios';
import './Branches.css';

// Manager role options
const MANAGER_ROLES = [
  { value: 'manager', label: 'Manager' },
  { value: 'inventory_manager', label: 'Inventory Manager' },
  { value: 'accounts_manager', label: 'Accounts Manager' }
];

const Branches = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success, error } = useNotification();
  const [branches, setBranches] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showManagersModal, setShowManagersModal] = useState(false);
  const [showAddManagerForm, setShowAddManagerForm] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchManagers, setBranchManagers] = useState([]);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    businessId: '',
    openingBalance: '',
    managerUsername: '',
    managerName: '',
    managerPhone: '',
    managerPassword: '',
    managerRole: 'manager'
  });
  const [newManagerData, setNewManagerData] = useState({
    username: '',
    name: '',
    phone: '',
    password: '',
    role: 'manager'
  });
  const [managerToDelete, setManagerToDelete] = useState(null);
  const [showDeleteManagerModal, setShowDeleteManagerModal] = useState(false);
  const [showBranchLedgerModal, setShowBranchLedgerModal] = useState(false);
  const [branchForLedger, setBranchForLedger] = useState(null);
  const [branchParty, setBranchParty] = useState(null);
  const [ledgerStatementFrom, setLedgerStatementFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [ledgerStatementTo, setLedgerStatementTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [ledgerStatementData, setLedgerStatementData] = useState(null);
  const [ledgerStatementLoading, setLedgerStatementLoading] = useState(false);

  const formatBalance = (balance, isShopExpense = false) => {
    const num = parseFloat(balance);
    if (isNaN(num) || isShopExpense) return <span className="balance-zero">{formatPKR(0)}</span>;
    if (num > 0) return <span className="balance-positive">{formatPKR(num)} (To receive)</span>;
    if (num < 0) return <span className="balance-negative">{formatPKR(Math.abs(num))} (We owe)</span>;
    return <span className="balance-zero">{formatPKR(0)}</span>;
  };

  useEffect(() => {
    fetchBranches();
    if (user.role === 'super_admin') {
      fetchBusinesses();
    }
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = branches.filter(branch =>
        branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.business?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.phone?.includes(searchTerm)
      );
      setFilteredBranches(filtered);
    } else {
      setFilteredBranches(branches);
    }
  }, [searchTerm, branches]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses/all`);
      setBusinesses(response.data.businesses || []);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/branches`);
      console.log('Branches API Full Response:', response);
      console.log('Branches API Response Data:', response.data);
      
      // Extract branches from response - handle different response structures
      let branchData = [];
      if (response.data) {
        if (Array.isArray(response.data.branches)) {
          branchData = response.data.branches;
        } else if (response.data.data && Array.isArray(response.data.data.branches)) {
          branchData = response.data.data.branches;
        } else if (Array.isArray(response.data)) {
          branchData = response.data;
        }
      }
      
      console.log('Parsed branches:', branchData);
      console.log('Branches count:', branchData.length);
      console.log('First branch:', branchData[0]);
      
      if (Array.isArray(branchData)) {
        setBranches(branchData);
        setFilteredBranches(branchData);
      } else {
        console.error('Branches data is not an array:', branchData);
        setBranches([]);
        setFilteredBranches([]);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
      error(getErrorMessage(err));
      setBranches([]);
      setFilteredBranches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      description: branch.description || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      businessId: branch.business_id || '',
      managerUsername: '',
      managerName: '',
      managerPhone: '',
      managerPassword: ''
    });
    setShowModal(true);
  };

  const handleView = (branch) => {
    navigate(`/branches/${branch.id}/details`);
  };

  const handleViewLedger = async (branch) => {
    try {
      const res = await axios.get(`${API}/branches/${branch.id}/party`);
      const party = res.data.party;
      if (!party) {
        error('Branch ledger not available for this branch.');
        return;
      }
      setBranchForLedger(branch);
      setBranchParty(party);
      setLedgerStatementData(null);
      setShowBranchLedgerModal(true);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleBranchLedgerApply = async () => {
    if (!branchParty) return;
    setLedgerStatementLoading(true);
    try {
      const res = await axios.get(`${API}/parties/${branchParty.id}/statement?from=${ledgerStatementFrom}&to=${ledgerStatementTo}`);
      setLedgerStatementData(res.data);
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setLedgerStatementLoading(false);
    }
  };

  const handleViewManagers = async (branch) => {
    setSelectedBranch(branch);
    try {
      const response = await axios.get(`${API}/branches/${branch.id}/managers`);
      setBranchManagers(response.data.managers || []);
      setShowManagersModal(true);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'inventory_manager': return 'role-inventory';
      case 'accounts_manager': return 'role-accounts';
      default: return 'role-manager';
    }
  };

  const getRoleLabel = (role) => {
    const found = MANAGER_ROLES.find(r => r.value === role);
    return found ? found.label : role;
  };

  const resetNewManagerForm = () => {
    setNewManagerData({
      username: '',
      name: '',
      phone: '',
      password: '',
      role: 'manager'
    });
    setShowAddManagerForm(false);
  };

  const handleAddManager = async (e) => {
    e.preventDefault();
    if (!selectedBranch) return;
    
    try {
      const payload = {
        branchId: selectedBranch.id,
        username: newManagerData.username.trim(),
        name: newManagerData.name.trim(),
        phone: newManagerData.phone.trim(),
        password: newManagerData.password,
        role: 'manager'
      };
      
      await axios.post(`${API}/branches/add-manager`, payload);
      success('Manager added successfully!');
      
      // Refresh managers list
      const response = await axios.get(`${API}/branches/${selectedBranch.id}/managers`);
      setBranchManagers(response.data.managers || []);
      
      // Reset form
      resetNewManagerForm();
      
      // Refresh branches to update the count
      fetchBranches();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleDelete = (branchId) => {
    setBranchToDelete(branchId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/branches/${branchToDelete}`);
      success('Branch deleted successfully!');
      setShowConfirmModal(false);
      setBranchToDelete(null);
      fetchBranches();
    } catch (err) {
      error(getErrorMessage(err));
      setShowConfirmModal(false);
      setBranchToDelete(null);
    }
  };

  const handleDeleteManager = (manager) => {
    setManagerToDelete(manager);
    setShowDeleteManagerModal(true);
  };

  const confirmDeleteManager = async () => {
    if (!selectedBranch || !managerToDelete) return;
    
    try {
      await axios.delete(`${API}/branches/${selectedBranch.id}/managers/${managerToDelete.id}`);
      success('Manager removed successfully!');
      
      // Refresh managers list
      const response = await axios.get(`${API}/branches/${selectedBranch.id}/managers`);
      setBranchManagers(response.data.managers || []);
      
      // Refresh branches to update the count
      fetchBranches();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setShowDeleteManagerModal(false);
      setManagerToDelete(null);
    }
  };

  // Utility function to trim all string values in an object
  const trimFormData = (data) => {
    const trimmed = {};
    for (const key in data) {
      if (typeof data[key] === 'string') {
        trimmed[key] = data[key].trim();
      } else {
        trimmed[key] = data[key];
      }
    }
    return trimmed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        const trimmedData = trimFormData({
          name: formData.name,
          description: formData.description,
          address: formData.address,
          phone: formData.phone,
          email: formData.email
        });
        await axios.put(`${API}/branches/${editingBranch.id}`, trimmedData);
        success('Branch updated successfully!');
      } else {
        const dataToSend = trimFormData(formData);
        if (user.role === 'admin') {
          // Admin doesn't need to specify businessId
          const { businessId, ...data } = dataToSend;
          await axios.post(`${API}/branches`, data);
        } else {
          await axios.post(`${API}/branches`, dataToSend);
        }
        success('Branch created successfully!');
      }
      setShowModal(false);
      setEditingBranch(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        businessId: '',
        openingBalance: '',
        managerUsername: '',
        managerName: '',
        managerPhone: '',
        managerPassword: '',
        managerRole: 'manager'
      });
      fetchBranches();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const canCreateBranch = user.role === 'super_admin' || user.role === 'admin';

  return (
    <Layout>
      <div className="branches-page">
        <div className="page-header">
          <div>
            <h1>Branches</h1>
            <p className="page-subtitle">Manage all branches in the system</p>
          </div>
          {canCreateBranch && (
            <Tooltip text="Create a new branch" position="left">
              <button onClick={() => setShowModal(true)} className="create-button">
                <HiPlus />
                <span>Create Branch</span>
              </button>
            </Tooltip>
          )}
        </div>

        <div className="search-bar-container">
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search branches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {loading ? (
          <Loader message="Loading branches..." variant="block" />
        ) : (
          <div className="table-container">
            {filteredBranches.length === 0 ? (
              <div className="empty-state">
                <p>No branches found.</p>
                {canCreateBranch && (
                  <button onClick={() => setShowModal(true)} className="empty-state-button">
                    Create Your First Branch
                  </button>
                )}
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Business</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Managers</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBranches.map((branch) => (
                    <tr key={branch.id}>
                      <td className="code-cell">BR-{String(branch.id).padStart(3, '0')}</td>
                      <td className="font-semibold">{branch.name}</td>
                      <td>{branch.business?.name || '—'}</td>
                      <td>{branch.email || '—'}</td>
                      <td>{branch.phone || '—'}</td>
                      <td>
                        <button 
                          className="managers-badge-btn"
                          onClick={() => handleViewManagers(branch)}
                          title="View managers"
                        >
                          <HiUserGroup />
                          <span>{branch.users?.filter(u => ['manager', 'inventory_manager', 'accounts_manager'].includes(u.role)).length || 0}</span>
                        </button>
                      </td>
                      <td>
                        <span className={`status-badge ${branch.is_active ? 'active' : 'inactive'}`}>
                          {branch.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="created-at-cell">{formatDate(branch.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <Tooltip text="Edit branch" position="top">
                            <button 
                              className="action-btn edit-btn" 
                              onClick={() => handleEdit(branch)}
                            >
                              <HiPencil />
                            </button>
                          </Tooltip>
                          <Tooltip text="View details" position="top">
                            <button 
                              className="action-btn view-btn" 
                              onClick={() => handleView(branch)}
                            >
                              <HiEye />
                            </button>
                          </Tooltip>
                          <Tooltip text="View ledger" position="top">
                            <button 
                              className="action-btn statement-btn" 
                              onClick={() => handleViewLedger(branch)}
                            >
                              <HiDocumentText />
                            </button>
                          </Tooltip>
                          <Tooltip text="Delete branch" position="top">
                            <button 
                              className="action-btn delete-btn" 
                              onClick={() => handleDelete(branch.id)}
                            >
                              <HiTrash />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingBranch(null); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingBranch ? 'Edit Branch' : 'Create Branch'}</h2>
              <form onSubmit={handleSubmit}>
                {user.role === 'super_admin' && !editingBranch && (
                  <div className="form-group">
                    <label>Business *</label>
                    <select
                      value={formData.businessId}
                      onChange={(e) => setFormData({ ...formData, businessId: e.target.value })}
                      required
                    >
                      <option value="">Select Business</option>
                      {businesses.map((business) => (
                        <option key={business.id} value={business.id}>
                          {business.name}
                        </option>
                      ))}
                    </select>
                    <p className="helper-text">Required. Select the business this branch belongs to.</p>
                  </div>
                )}
                <div className="form-group">
                  <label>Branch Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Main Branch, Downtown Office"
                    required
                  />
                  <p className="helper-text">Required. 2-100 characters. Letters, numbers, and spaces allowed.</p>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter branch description (optional)"
                    rows="3"
                  />
                  <p className="helper-text">Optional. Maximum 500 characters. Any text allowed.</p>
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="e.g., 123 Main Street, City, Country"
                  />
                  <p className="helper-text">Optional. Maximum 200 characters. Street address, city, country.</p>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="e.g., +1234567890 or (123) 456-7890"
                  />
                  <p className="helper-text">Optional. Maximum 20 characters. Numbers, spaces, dashes, parentheses, and + sign allowed.</p>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g., branch@example.com"
                  />
                  <p className="helper-text">Optional. Valid email format required (e.g., user@domain.com).</p>
                </div>
                {!editingBranch && (
                  <div className="form-group">
                    <label>Opening balance (payables/receivables)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.openingBalance}
                      onChange={(e) => setFormData({ ...formData, openingBalance: e.target.value })}
                      placeholder="0"
                    />
                    <p className="helper-text">Optional. Positive = receivable (they owe us), negative = payable (we owe them). Used for branch ledger.</p>
                  </div>
                )}
                {!editingBranch && (
                  <>
                    <h3>Manager Details (Optional)</h3>
                    <p className="helper-text" style={{ marginBottom: 10 }}>New manager will have the Manager role. Username must be unique across all users.</p>
                    <div className="form-group">
                      <label>Manager Username</label>
                      <input
                        type="text"
                        value={formData.managerUsername}
                        onChange={(e) => setFormData({ ...formData, managerUsername: e.target.value })}
                        placeholder="e.g., test_manager1 or manager123"
                      />
                      <p className="helper-text">Optional. 3-30 characters. Letters, numbers, and underscores allowed (no spaces or other special characters).</p>
                    </div>
                    <div className="form-group">
                      <label>Manager Name</label>
                      <input
                        type="text"
                        value={formData.managerName}
                        onChange={(e) => setFormData({ ...formData, managerName: e.target.value })}
                        placeholder="e.g., John Doe"
                      />
                      <p className="helper-text">Optional. Maximum 100 characters. Full name of the manager.</p>
                    </div>
                    <div className="form-group">
                      <label>Manager Phone</label>
                      <input
                        type="tel"
                        value={formData.managerPhone}
                        onChange={(e) => setFormData({ ...formData, managerPhone: e.target.value })}
                        placeholder="e.g., +1234567890 or (123) 456-7890"
                      />
                      <p className="helper-text">Optional. Maximum 20 characters. Numbers, spaces, dashes, parentheses, and + sign allowed.</p>
                    </div>
                    <div className="form-group">
                      <label>Manager Password</label>
                      <input
                        type="text"
                        value={formData.managerPassword}
                        onChange={(e) => setFormData({ ...formData, managerPassword: e.target.value })}
                        placeholder="e.g., SecurePass123"
                      />
                      <p className="helper-text">Optional. Minimum 6 characters. Any characters allowed. Will be hashed for security.</p>
                    </div>
                  </>
                )}
                <div className="modal-actions">
                  <button type="button" onClick={() => { setShowModal(false); setEditingBranch(null); }}>Cancel</button>
                  <button type="submit">{editingBranch ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => { setShowConfirmModal(false); setBranchToDelete(null); }}
          onConfirm={confirmDelete}
          title="Delete Branch"
          message="Are you sure you want to delete this branch? This will soft delete it."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />

        {/* Branch Ledger / Statement Modal */}
        {showBranchLedgerModal && branchForLedger && branchParty && (
          <div className="modal-overlay" onClick={() => { setShowBranchLedgerModal(false); setBranchForLedger(null); setBranchParty(null); setLedgerStatementData(null); }}>
            <div className="modal-content modal-xlarge branch-ledger-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Branch ledger – {branchForLedger.name}</h2>
              <p className="branch-ledger-hint">Ledger for this branch (stock transfers and branch-level transactions).</p>
              <div className="statement-controls">
                <div className="form-group">
                  <label>From</label>
                  <input
                    type="date"
                    value={ledgerStatementFrom}
                    onChange={(e) => setLedgerStatementFrom(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>To</label>
                  <input
                    type="date"
                    value={ledgerStatementTo}
                    onChange={(e) => setLedgerStatementTo(e.target.value)}
                  />
                </div>
                <button type="button" className="btn-primary" onClick={handleBranchLedgerApply} disabled={ledgerStatementLoading}>
                  {ledgerStatementLoading ? 'Loading…' : 'Apply'}
                </button>
              </div>
              {ledgerStatementLoading && !ledgerStatementData ? (
                <Loader message="Loading statement…" variant="block" />
              ) : ledgerStatementData ? (
                <div className="statement-body">
                  <div className="statement-period">
                    Period: {ledgerStatementData.statement_from} to {ledgerStatementData.statement_to}
                  </div>
                  <div className="statement-table-wrap">
                    <table className="ledger-table statement-table">
                      <thead>
                        <tr>
                          <th className="col-date">Date</th>
                          <th className="col-type">Type</th>
                          <th className="col-bill">Bill / Reference</th>
                          <th className="col-desc">Description</th>
                          <th className="col-amount">Amount</th>
                          <th className="col-balance">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(ledgerStatementData.ledger || []).map((entry) => {
                          const dr = parseFloat(entry.debit) || 0;
                          const cr = parseFloat(entry.credit) || 0;
                          const amount = dr > 0 ? dr : cr;
                          const billRef = entry.sale_details
                            ? `Sale #${entry.sale_details.invoice_number || entry.reference_id} · ${formatPKR(entry.sale_details.total_amount || amount)}`
                            : entry.purchase_details
                              ? `Purchase #${entry.purchase_details.purchase_number || entry.reference_id} · ${formatPKR(entry.purchase_details.total_amount || amount)}`
                              : (entry.transaction_type === 'payment_in' || entry.transaction_type === 'payment_out')
                                ? `Payment · ${formatPKR(amount)}`
                                : entry.transaction_type === 'opening_balance'
                                  ? `Opening · ${formatPKR(amount)}`
                                  : entry.transaction_type === 'balance_adjustment'
                                    ? `Balance edited · ${formatPKR(amount)}`
                                    : amount > 0 ? formatPKR(amount) : '—';
                          return (
                            <tr key={entry.id}>
                              <td>{new Date(entry.transaction_date).toLocaleDateString()}</td>
                              <td><span className={`type-badge ${(entry.transaction_type || '').replace(/\s+/g, '_')}`}>{entry.transaction_type === 'balance_adjustment' ? 'Balance edited' : (entry.transaction_type || '').replace(/_/g, ' ')}</span></td>
                              <td className="statement-bill-ref">{billRef}</td>
                              <td>{entry.description || '—'}</td>
                              <td className={dr > 0 ? 'debit amount-cell' : 'credit amount-cell'}>
                                {amount > 0 ? <>{formatPKR(amount)} <span className="dr-cr-label">{dr > 0 ? 'Dr' : 'Cr'}</span></> : '—'}
                              </td>
                              <td className="balance-cell">{formatBalance(entry.balance_after)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {(ledgerStatementData.ledger || []).length === 0 && (
                    <p className="no-entries">No transactions in this period.</p>
                  )}
                  <div className="statement-footer">
                    <strong>Current balance:</strong> {formatBalance(ledgerStatementData.party?.balance)}
                  </div>
                </div>
              ) : (
                <div className="empty-state">Select period and click Apply to load ledger.</div>
              )}
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowBranchLedgerModal(false); setBranchForLedger(null); setBranchParty(null); setLedgerStatementData(null); }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Managers Modal */}
        {showManagersModal && selectedBranch && (
          <div className="modal-overlay" onClick={() => { setShowManagersModal(false); resetNewManagerForm(); }}>
            <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-with-action">
                <h2>Managers - {selectedBranch.name}</h2>
                {canCreateBranch && !showAddManagerForm && (
                  <button 
                    className="add-manager-btn"
                    onClick={() => setShowAddManagerForm(true)}
                  >
                    <HiPlus /> Add Manager
                  </button>
                )}
              </div>
              
              {/* Add Manager Form */}
              {showAddManagerForm && (
                <form onSubmit={handleAddManager} className="add-manager-form">
                  <h3>Add New Manager</h3>
                  <p className="add-manager-hint">Username must be unique. Once a manager is deleted, their credentials cannot be used to log in again.</p>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Username *</label>
                      <input
                        type="text"
                        value={newManagerData.username}
                        onChange={(e) => setNewManagerData({ ...newManagerData, username: e.target.value })}
                        placeholder="e.g. john_manager (unique for every user)"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Full Name</label>
                      <input
                        type="text"
                        value={newManagerData.name}
                        onChange={(e) => setNewManagerData({ ...newManagerData, name: e.target.value })}
                        placeholder="e.g., John Doe"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={newManagerData.phone}
                        onChange={(e) => setNewManagerData({ ...newManagerData, phone: e.target.value })}
                        placeholder="e.g., +923001234567"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Password *</label>
                    <input
                      type="password"
                      value={newManagerData.password}
                      onChange={(e) => setNewManagerData({ ...newManagerData, password: e.target.value })}
                      placeholder="Minimum 6 characters"
                      required
                    />
                  </div>
                  <div className="form-actions">
                    <button type="button" className="btn-secondary" onClick={resetNewManagerForm}>Cancel</button>
                    <button type="submit" className="btn-primary">Add Manager</button>
                  </div>
                </form>
              )}
              
              {/* Managers List */}
              {branchManagers.length === 0 && !showAddManagerForm ? (
                <div className="empty-managers">
                  <p>No managers assigned to this branch yet.</p>
                  {canCreateBranch && (
                    <button 
                      className="add-first-manager-btn"
                      onClick={() => setShowAddManagerForm(true)}
                    >
                      <HiPlus /> Add First Manager
                    </button>
                  )}
                </div>
              ) : (
                <div className="managers-list">
                  {branchManagers.map((manager) => (
                    <div key={manager.id} className="manager-card">
                      <div className="manager-info">
                        <div className="manager-name">{manager.name || manager.username}</div>
                        <div className="manager-username">@{manager.username}</div>
                        {manager.phone && <div className="manager-phone">{manager.phone}</div>}
                      </div>
                      <div className="manager-actions">
                        <span className={`role-badge ${getRoleBadgeClass(manager.role)}`}>
                          {getRoleLabel(manager.role)}
                        </span>
                        <span className={`status-dot ${manager.is_active ? 'active' : 'inactive'}`}></span>
                        {canCreateBranch && (
                          <Tooltip text="Remove manager" position="top">
                            <button 
                              className="delete-manager-btn"
                              onClick={() => handleDeleteManager(manager)}
                            >
                              <HiTrash />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowManagersModal(false); resetNewManagerForm(); }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Manager Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteManagerModal}
          onClose={() => { setShowDeleteManagerModal(false); setManagerToDelete(null); }}
          onConfirm={confirmDeleteManager}
          title="Remove Manager"
          message={`Are you sure you want to remove ${managerToDelete?.name || managerToDelete?.username} from this branch?`}
          confirmText="Remove"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default Branches;
